/**
 * Parsing for the filter builder's "is one of" / "is none of" id-list input.
 *
 * People paste ids from all over: a comma list typed by hand, a column copied
 * out of a spreadsheet (one id per line), a list pulled from a log (spaces).
 * So commas, semicolons, spaces, tabs and line breaks all separate ids.
 */

export interface ParsedIdList {
  /** Whole-number ids in the order first typed, with duplicates removed. */
  ids: number[];
  /** Tokens that are not whole numbers up to MAX_ID, so the input can say what it dropped. */
  invalidTokens: string[];
}

const ID_SEPARATOR = /[\s,;]+/;
const WHOLE_NUMBER = /^\d+$/;

/**
 * The largest id the list accepts. Most id fields are mapped `integer` in Elasticsearch, and a
 * terms query holding a larger value fails the whole search with "out of range for an integer".
 * Leaving the token out, and listing it in red, is better than an error page.
 */
export const MAX_ID = 2_147_483_647;

export function parseIdList(text: string): ParsedIdList {
  const ids: number[] = [];
  const invalidTokens: string[] = [];
  const seenIds = new Set<number>();

  for (const token of text.split(ID_SEPARATOR)) {
    if (token === '') {
      continue;
    }
    // Measured on the digits first, leading zeros aside: Number() rounds anything past 2^53.
    const significantDigits = token.replace(/^0+(?=\d)/, '');
    const numericId =
      WHOLE_NUMBER.test(token) && significantDigits.length <= String(MAX_ID).length ? Number(token) : NaN;
    if (!(numericId <= MAX_ID)) {
      invalidTokens.push(token);
      continue;
    }
    if (!seenIds.has(numericId)) {
      seenIds.add(numericId);
      ids.push(numericId);
    }
  }

  return { ids, invalidTokens };
}

/**
 * Coerces whatever a rule already holds into an id array. A rule arriving from
 * "is equal to" holds a scalar; a saved favorite may hold strings.
 */
export function idListFromRuleValue(value: unknown): number[] {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  const rawValues = Array.isArray(value) ? value : [value];
  return parseIdList(rawValues.map(String).join(',')).ids;
}

/** How the input shows a stored list, e.g. "34187, 15822". */
export function formatIdList(ids: number[]): string {
  return ids.join(', ');
}
